import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { AlertTriangle, ClipboardPaste, Check } from 'lucide-react';
import { formatCurrency } from '../lib/format';

// Canonical header order for the 9-column schema
const SCHEMA = [
  { key: 'institution',    label: 'Institution',      type: 'text' },
  { key: 'holder',         label: 'Holder',           type: 'dd',   dd: 'holder' },
  { key: 'investmentName', label: 'Investment Name',  type: 'text' },
  { key: 'mainAssetClass', label: 'Main Asset Class', type: 'dd',   dd: 'mainAssetClass' },
  { key: 'subAssetClass',  label: 'Sub-Asset Class',  type: 'dd',   dd: 'subAssetClass' },
  { key: 'marketCap',      label: 'Strategy',         type: 'dd',   dd: 'marketCap' },
  { key: 'geography',      label: 'Geography',        type: 'dd',   dd: 'geography' },
  { key: 'cost',           label: 'Cost',             type: 'num' },
  { key: 'marketValue',    label: 'Market Value',     type: 'num' },
];

const HEADER_HINTS = {
  institution: ['institution', 'broker'],
  holder: ['holder', 'owner', 'name'],
  investmentName: ['investment', 'asset', 'security', 'stock'],
  mainAssetClass: ['main', 'class'],
  subAssetClass: ['sub'],
  marketCap: ['strategy', 'market', 'cap'],
  geography: ['geography', 'geo', 'country', 'region'],
  cost: ['cost', 'invested', 'purchase'],
  marketValue: ['market value', 'value', 'mv', 'current'],
};

// Best-effort: detect whether first row looks like a header
const looksLikeHeader = (cells) => {
  if (!cells) return false;
  const lower = cells.map((c) => String(c || '').trim().toLowerCase());
  let hits = 0;
  for (const col of SCHEMA) {
    const hints = HEADER_HINTS[col.key] || [col.label.toLowerCase()];
    if (lower.some((cell) => hints.some((h) => cell.includes(h)))) hits++;
  }
  return hits >= 4;
};

// Parse pasted text: tab-separated (Excel default) or comma-separated fallback
const parseText = (text) => {
  const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.length > 0);
  if (lines.length === 0) return [];
  const useTab = lines.some((l) => l.includes('\t'));
  const sep = useTab ? '\t' : ',';
  return lines.map((l) => l.split(sep).map((c) => c.trim()));
};

// If a header row is present, build a reorder map from its column positions
// to the canonical schema order. Otherwise assume rows are in SCHEMA order.
const buildColumnOrder = (headerCells) => {
  if (!headerCells) return null;
  const order = new Array(SCHEMA.length).fill(-1);
  const used = new Set();
  headerCells.forEach((h, srcIdx) => {
    const lower = String(h || '').toLowerCase();
    for (let i = 0; i < SCHEMA.length; i++) {
      if (used.has(i)) continue;
      const hints = HEADER_HINTS[SCHEMA[i].key] || [SCHEMA[i].label.toLowerCase()];
      if (hints.some((hint) => lower.includes(hint))) {
        order[i] = srcIdx;
        used.add(i);
        break;
      }
    }
  });
  return order;
};

const numParse = (s) => {
  if (s === null || s === undefined) return 0;
  const cleaned = String(s).replace(/[,\s₹$]/g, '').replace(/[()]/g, '-');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const rowsFromParsed = (grid, colOrder) => {
  return grid.map((cells) => {
    const pick = (canonicalIdx) => {
      const srcIdx = colOrder ? colOrder[canonicalIdx] : canonicalIdx;
      if (srcIdx < 0 || srcIdx >= cells.length) return '';
      return cells[srcIdx];
    };
    return {
      institution: pick(0),
      holder: pick(1),
      investmentName: pick(2),
      mainAssetClass: pick(3),
      subAssetClass: pick(4),
      marketCap: pick(5),
      geography: pick(6),
      cost: numParse(pick(7)),
      marketValue: numParse(pick(8)),
    };
  });
};

const validateRows = (rows, dropdowns) => {
  const errors = [];
  rows.forEach((r, idx) => {
    const line = idx + 1;
    if (!r.investmentName) errors.push({ line, field: 'Investment Name', msg: 'required' });
    for (const col of SCHEMA) {
      if (col.type === 'dd' && r[col.key]) {
        const list = dropdowns[col.dd] || [];
        if (!list.includes(r[col.key])) {
          errors.push({ line, field: col.label, msg: `"${r[col.key]}" not in Dropdown Master` });
        }
      }
    }
  });
  return errors;
};

export default function PasteRowsDialog({ open, onOpenChange, dropdowns, activeRowIdx, totalRows, onCommit }) {
  const [text, setText] = useState('');
  const [mode, setMode] = useState('replace'); // 'replace' | 'add'
  const [treatFirstAsHeader, setTreatFirstAsHeader] = useState('auto'); // 'auto' | 'yes' | 'no'

  useEffect(() => {
    if (open) {
      setText('');
      setMode('replace');
      setTreatFirstAsHeader('auto');
    }
  }, [open]);

  const parsed = useMemo(() => {
    const grid = parseText(text);
    if (grid.length === 0) return { rows: [], errors: [], hasHeader: false, colOrder: null };

    let hasHeader;
    if (treatFirstAsHeader === 'yes') hasHeader = true;
    else if (treatFirstAsHeader === 'no') hasHeader = false;
    else hasHeader = looksLikeHeader(grid[0]);

    const colOrder = hasHeader ? buildColumnOrder(grid[0]) : null;
    const body = hasHeader ? grid.slice(1) : grid;
    const rows = rowsFromParsed(body, colOrder);
    const errors = validateRows(rows, dropdowns);
    return { rows, errors, hasHeader, colOrder };
  }, [text, treatFirstAsHeader, dropdowns]);

  const hasCursor = activeRowIdx !== null && activeRowIdx !== undefined && activeRowIdx >= 0 && activeRowIdx < totalRows;

  const targetDescription = useMemo(() => {
    if (mode === 'replace') {
      if (hasCursor) return `Replace rows ${activeRowIdx + 1} and below. Rows 1–${activeRowIdx} stay as-is.`;
      return `Replace all ${totalRows} existing rows.`;
    }
    if (hasCursor) return `Insert new rows at row ${activeRowIdx + 1}. Existing rows from ${activeRowIdx + 1} onward will shift down.`;
    return `Append at the end of the grid.`;
  }, [mode, hasCursor, activeRowIdx, totalRows]);

  const commit = () => {
    if (parsed.rows.length === 0) return;
    onCommit({ rows: parsed.rows, mode, activeRowIdx: hasCursor ? activeRowIdx : null });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="paste-rows-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <ClipboardPaste size={16} /> Paste rows from another source
          </DialogTitle>
          <DialogDescription>
            Paste a block copied from Excel, Google Sheets or any of the other data entry grids. Tab-separated is preferred (Excel default), commas also work.
            <br />
            Expected 9 columns in this order:
            <span className="font-mono text-[10px] text-[#575757] block mt-1">
              Institution · Holder · Investment Name · Main Asset Class · Sub-Asset Class · Strategy · Geography · Cost · Market Value
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="overline">Paste here</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              placeholder={"Paste rows (tab-separated) here…\ne.g. Zerodha Kite\tRahul Patni\tReliance Industries\tEquity\tStock - Direct\tLarge cap\tIndia\t1800000\t2100000"}
              className="font-mono text-xs rounded-sm"
              data-testid="paste-textarea"
            />
            <div className="flex items-center justify-between mt-2 text-[11px] text-[#787872] font-mono">
              <span>
                {parsed.rows.length > 0 ? (
                  <>Detected {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'}{parsed.hasHeader ? ' · header row auto-detected' : ''}</>
                ) : 'Paste something to preview'}
              </span>
              <div className="flex items-center gap-3">
                <span>First row is header:</span>
                {['auto', 'yes', 'no'].map((v) => (
                  <button
                    key={v}
                    onClick={() => setTreatFirstAsHeader(v)}
                    className={`chip cursor-pointer ${treatFirstAsHeader === v ? '' : 'opacity-60 hover:opacity-100'}`}
                    style={treatFirstAsHeader === v ? { background: '#1A3626', color: '#fff', borderColor: '#1A3626' } : {}}
                    data-testid={`paste-header-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {parsed.errors.length > 0 && (
            <div className="border border-[hsl(var(--destructive))] bg-[#FDF2F2] p-3 max-h-36 overflow-y-auto text-xs font-mono">
              <div className="flex items-center gap-2 mb-1 text-[#7A1F1F]">
                <AlertTriangle size={12} /> {parsed.errors.length} validation warning{parsed.errors.length === 1 ? '' : 's'}
              </div>
              {parsed.errors.slice(0, 12).map((e, i) => (
                <div key={i} className="text-[#7A1F1F]">
                  Row {e.line} · {e.field}: {e.msg}
                </div>
              ))}
              {parsed.errors.length > 12 && (
                <div className="text-[#7A1F1F]">+ {parsed.errors.length - 12} more…</div>
              )}
            </div>
          )}

          {parsed.rows.length > 0 && (
            <div>
              <Label className="overline">Preview</Label>
              <div className="max-h-[240px] overflow-auto border border-border mt-1">
                <table className="grid-table">
                  <thead>
                    <tr>
                      {SCHEMA.map((c) => (
                        <th key={c.key} className={c.type === 'num' ? 'num' : ''}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 50).map((r, i) => (
                      <tr key={i}>
                        {SCHEMA.map((c) => {
                          if (c.type === 'num') {
                            return <td key={c.key} className="num font-mono tabular-nums">{formatCurrency(r[c.key], 'INR', { mode: 'full' })}</td>;
                          }
                          return <td key={c.key}>{r[c.key] || <span className="text-[#AAA]">—</span>}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.rows.length > 50 && (
                  <div className="px-3 py-2 text-xs text-[#787872] font-mono border-t border-border bg-[#FAFAF8]">
                    + {parsed.rows.length - 50} more row{parsed.rows.length - 50 === 1 ? '' : 's'} (commit to load all)
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <Label className="overline mb-2 block">Import mode</Label>
            <RadioGroup value={mode} onValueChange={setMode} className="gap-2">
              <label className="border border-border bg-white px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-[#FAFAF8]" data-testid="paste-mode-replace">
                <RadioGroupItem value="replace" className="mt-0.5" />
                <div>
                  <div className="font-heading font-semibold text-[13px]">Replace existing rows</div>
                  <div className="text-[11px] text-[#575757] font-mono mt-0.5">
                    {hasCursor
                      ? `Row ${activeRowIdx + 1} onwards will be overwritten. Rows above stay.`
                      : `All ${totalRows} current rows will be wiped and replaced.`}
                  </div>
                </div>
              </label>
              <label className="border border-border bg-white px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-[#FAFAF8]" data-testid="paste-mode-add">
                <RadioGroupItem value="add" className="mt-0.5" />
                <div>
                  <div className="font-heading font-semibold text-[13px]">Add new rows</div>
                  <div className="text-[11px] text-[#575757] font-mono mt-0.5">
                    {hasCursor
                      ? `Insert at row ${activeRowIdx + 1}. Existing rows from ${activeRowIdx + 1} onward will shift down.`
                      : `Appended at the end of the grid.`}
                  </div>
                </div>
              </label>
            </RadioGroup>
            <div className="mt-3 text-[11px] font-mono text-[hsl(var(--primary))] flex items-center gap-1.5">
              <Check size={12} /> {targetDescription}
            </div>
          </div>
        </div>

        <DialogFooter>
          <button className="btn-secondary" onClick={() => onOpenChange(false)}>Cancel</button>
          <button
            className={`btn-primary ${parsed.rows.length === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
            onClick={commit}
            disabled={parsed.rows.length === 0}
            data-testid="paste-commit-button"
          >
            {mode === 'replace'
              ? `Replace with ${parsed.rows.length} row${parsed.rows.length === 1 ? '' : 's'}`
              : `Add ${parsed.rows.length} row${parsed.rows.length === 1 ? '' : 's'}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
